import React, { useState } from 'react';
import { useNavigate, Navigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff, Mail, Lock, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth, roleHomePath } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { CapinLogo } from '@/components/brand/CapinLogo';
import { toast } from 'sonner';
import { RegisterPanel } from '@/pages/auth/Register';

export default function Login() {
  const { user, login, loading } = useAuth();
  const { branding } = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const startTab = location?.state?.tab === 'register' ? 'register' : 'signin';
  const [tab, setTab] = useState(startTab);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPwd, setShowPwd] = useState(false);

  if (user) return <Navigate to={roleHomePath(user.role)} replace />;

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome, ${u.name}`);
      navigate(roleHomePath(u.role), { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Login failed');
    }
  };

  return (
    <div className="min-h-dvh grid lg:grid-cols-2 bg-white">
      {/* Brand panel */}
      <div className="relative hidden lg:block overflow-hidden" style={{ minHeight: '100vh' }}>
        <div className="absolute inset-0" style={{ backgroundImage: `url(${branding.background_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(120deg, rgba(10,37,64,0.86) 0%, rgba(10,37,64,0.65) 60%, rgba(10,37,64,0.45) 100%)' }} />
        <div className="relative h-full flex flex-col p-10 xl:p-14 text-white">
          <div className="flex items-center gap-3">
            <CapinLogo size={56} monogram={branding.logo_monogram} imageUrl={branding.logo_url} />
            <div>
              <div className="text-xl font-semibold tracking-tight">{branding.brand_name} <span style={{ color: '#d4a017' }}>{branding.brand_suffix}</span></div>
              <div className="text-[11px] uppercase tracking-[0.25em] text-white/70">{branding.location_text}</div>
            </div>
          </div>

          <div className="mt-auto">
            <h1 className="font-display text-4xl xl:text-6xl font-semibold leading-[1.05]">
              <span className="block">{branding.tagline_top}</span>
              <span className="block" style={{ color: '#d4a017' }}>{branding.tagline_accent}</span>
              <span className="block">{branding.tagline_bottom}</span>
            </h1>
            <p className="mt-5 max-w-md text-white/85 text-base leading-relaxed">{branding.subtitle}</p>

            <div className="mt-10 grid grid-cols-3 gap-6 max-w-md">
              {(branding.stats || []).map((s, i) => (
                <div key={`${s.label}-${i}`}>
                  <div className="font-display text-3xl xl:text-4xl font-semibold" style={{ color: '#d4a017' }}>{s.value}</div>
                  <div className="text-xs text-white/80 mt-1">{s.label}</div>
                </div>
              ))}
            </div>

            <div className="mt-12 text-xs text-white/70">{branding.footer_text}</div>
            <div className="text-xs text-white/60">{branding.location_text}</div>
          </div>
        </div>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-5 sm:p-10">
        <Card className="w-full max-w-md p-5 sm:p-7 border-0 shadow-2xl rounded-2xl" style={{ boxShadow: '0 24px 70px -20px rgba(10, 37, 64, 0.25)' }}>
          <div className="lg:hidden flex items-center gap-3 mb-4">
            <CapinLogo size={42} monogram={branding.logo_monogram} imageUrl={branding.logo_url} />
            <div className="text-lg font-semibold">{branding.brand_name} <span style={{ color: '#d4a017' }}>{branding.brand_suffix}</span></div>
          </div>

          {/* Pill tabs */}
          <div className="grid grid-cols-2 rounded-2xl p-1 bg-slate-50 border">
            <button onClick={() => setTab('signin')} data-testid="tab-signin"
                    className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === 'signin' ? 'bg-[#0a2540] text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
              Sign In
            </button>
            <button onClick={() => setTab('register')} data-testid="tab-register"
                    className={`py-2.5 rounded-xl text-sm font-medium transition-colors ${tab === 'register' ? 'bg-[#0a2540] text-white shadow-sm' : 'text-slate-600 hover:text-slate-800'}`}>
              Register
            </button>
          </div>

          {tab === 'signin' ? (
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="email" className="text-[#0a2540] font-medium">Email Address <span className="text-[#d4a017]">*</span></Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="email" type="email" autoComplete="email" required value={email}
                         onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com"
                         className="pl-9 h-11 rounded-xl bg-slate-50/60 border-slate-200"
                         data-testid="login-email-input" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="password" className="text-[#0a2540] font-medium">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input id="password" type={showPwd ? 'text' : 'password'} required value={password}
                         onChange={(e) => setPassword(e.target.value)} placeholder="Enter your password"
                         autoComplete="current-password"
                         className="pl-9 pr-10 h-11 rounded-xl bg-slate-50/60 border-slate-200"
                         data-testid="login-password-input" />
                  <button type="button" onClick={() => setShowPwd((v) => !v)}
                          className="absolute right-2.5 top-1/2 -translate-y-1/2 p-1.5 text-slate-400 hover:text-slate-600"
                          aria-label="Toggle password visibility">
                    {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-sm font-medium" style={{ color: '#0a2540' }} data-testid="forgot-password-link">Forgot password?</Link>
              </div>
              <Button type="submit" disabled={loading} data-testid="login-submit-button"
                      className="w-full h-12 rounded-xl text-white font-semibold"
                      style={{ background: 'linear-gradient(180deg, #11325c 0%, #0a2540 100%)' }}>
                {loading ? 'Signing in…' : (<>Sign In <ArrowRight className="ml-1 h-4 w-4" /></>)}
              </Button>
            </form>
          ) : (
            <RegisterPanel />
          )}
        </Card>
      </div>
    </div>
  );
}
